import React from 'react';
import { Select } from 'antd';
import { Menu, MenuButton, SubMenu, MenuItem } from '@szhsin/react-menu';
import { CloseOutlined } from '@ant-design/icons';
import '@szhsin/react-menu/dist/index.css';
import opsProm from '../locales/operations.promql.json';
import opsLoki from '../locales/operations.loki.json';

function findAllOps(mode){
  const spec = mode === 'promql' ? opsProm : opsLoki;
  return Object.values(spec).flat();
}

export default function OperationItem({mode='promql', op, index, onChangeOpType, onUpdateParam, onRemove, labelKeys, groupingLabelKeys, rangeOptions}){
  const allOps = findAllOps(mode);
  const byLabelOptions = (groupingLabelKeys ?? labelKeys ?? []).map((k) => ({ value: k, label: k }));

  function handleSwitch(opId){
    const newSpec = allOps.find(x=> x.id === opId); 
    if(newSpec){
      const params = {};
      if(newSpec.needsRange) params.range = rangeOptions?.[0] || '$__auto';
      if(newSpec.needsLabel) params.by = [];
      if(newSpec.input) params.value = ''; 
      onChangeOpType(index, {...newSpec, params});
    }
  }

  return (
    <div className='op-card'>
      <div className='op-card-top'>
        <Menu menuButton={<MenuButton className='op-title'>{op.display || op.id} ▾</MenuButton>} transition>
          {Object.entries(mode === 'promql' ? opsProm : opsLoki).map(([cat, items]) => (
            <SubMenu key={cat} label={cat}>
              {items.map(it=> <MenuItem key={it.id} onClick={()=> handleSwitch(it.id)}>{it.display || it.id}</MenuItem>)}
            </SubMenu>
          ))}
        </Menu>
        <div className='op-actions'>
          <button className='btn small op-remove-btn' onClick={()=> onRemove(index)} type="button" title="Remove operation"><CloseOutlined /></button>
        </div>
      </div>

      <div className='op-card-body'>
        {op.params?.by !== undefined && (
          <div className='op-row'>
            <label>By</label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select grouping labels"
              value={op.params.by || []}
              onChange={(vals) => onUpdateParam(index, 'by', vals)}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || option?.value || '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              style={{ width: '100%', minWidth: 220 }}
              maxTagCount="responsive"
              options={byLabelOptions}
            />
          </div>
        )}

        {op.params?.range !== undefined && (
          <div className='op-row'>
            <label>Range</label>
            <select value={op.params.range || ''} onChange={(e)=> onUpdateParam(index,'range', e.target.value)}>
              {rangeOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        {op.input && op.input === 'text' && (
          <div className='op-row'>
            <label>Value</label>
            <input value={op.params?.value || ''} onChange={(e)=> onUpdateParam(index,'value', e.target.value)} placeholder='text' />
          </div>
        )}
        {op.input && op.input === 'regex' && (
          <div className='op-row'>
            <label>Regex</label>
            <input value={op.params?.value || ''} onChange={(e)=> onUpdateParam(index,'value', e.target.value)} placeholder='regex' />
          </div>
        )}
                {/* K parameter for topk/bottomk */}
        {(op.id === 'topk' || op.id === 'bottomk') && (
          <div className='op-row'>
            <label>K (top/bottom N)</label>
            <input 
              type="number" 
              value={op.params?.k || 5} 
              onChange={(e)=> onUpdateParam(index,'k', parseInt(e.target.value))} 
              placeholder='5' 
              min="1"
            />
          </div>
        )}
        {/* Q parameter for quantile operations */}
        {(op.id === 'quantile' || op.id === 'quantile_over_time' || op.id === 'histogram_quantile') && (
          <div className='op-row'>
            <label>Quantile (0-1)</label>
            <input 
              type="number" 
              value={op.params?.q || 0.5} 
              onChange={(e)=> onUpdateParam(index,'q', parseFloat(e.target.value))} 
              placeholder='0.5' 
              min="0" 
              max="1" 
              step="0.1"
            />
          </div>
        )}
        {/* Min/Max for clamp */}
        {op.id === 'clamp' && (
          <>
            <div className='op-row'>
              <label>Min</label>
              <input 
                type="number" 
                value={op.params?.min || 0} 
                onChange={(e)=> onUpdateParam(index,'min', parseFloat(e.target.value))} 
                placeholder='0' 
              />
            </div>
            <div className='op-row'>
              <label>Max</label>
              <input 
                type="number" 
                value={op.params?.max || 1} 
                onChange={(e)=> onUpdateParam(index,'max', parseFloat(e.target.value))} 
                placeholder='1' 
              />
            </div>
          </>
        )}
        {/* Min for clamp_min */}
        {op.id === 'clamp_min' && (
          <div className='op-row'>
            <label>Min</label>
            <input 
              type="number" 
              value={op.params?.min || 0} 
              onChange={(e)=> onUpdateParam(index,'min', parseFloat(e.target.value))} 
              placeholder='0' 
            />
          </div>
        )}
        {/* Max for clamp_max */}
        {op.id === 'clamp_max' && (
          <div className='op-row'>
            <label>Max</label>
            <input 
              type="number" 
              value={op.params?.max || 1} 
              onChange={(e)=> onUpdateParam(index,'max', parseFloat(e.target.value))} 
              placeholder='1' 
            />
          </div>
        )}
        {/* T parameter for predict_linear */}
        {op.id === 'predict_linear' && (
          <div className='op-row'>
            <label>Seconds to predict</label>
            <input 
              type="number" 
              value={op.params?.t || 3600} 
              onChange={(e)=> onUpdateParam(index,'t', parseInt(e.target.value))} 
              placeholder='3600' 
              min="1"
            />
          </div>
        )}
        {op.id === 'label_replace' && (
          <>
            <div className='op-row'>
              <label>Destination Label</label>
              <input value={op.params?.dst || ''} onChange={(e)=> onUpdateParam(index,'dst', e.target.value)} placeholder='dst_label' />
            </div>
            <div className='op-row'>
              <label>Replacement</label>
              <input value={op.params?.replacement || ''} onChange={(e)=> onUpdateParam(index,'replacement', e.target.value)} placeholder='$1' />
            </div>
            <div className='op-row'>
              <label>Source Label</label>
              <input value={op.params?.src || ''} onChange={(e)=> onUpdateParam(index,'src', e.target.value)} placeholder='src_label' />
            </div>
            <div className='op-row'>
              <label>Regex</label>
              <input value={op.params?.regex || ''} onChange={(e)=> onUpdateParam(index,'regex', e.target.value)} placeholder='(.*)' />
            </div>
          </>
        )}

        {op.id === 'histogram_fraction' && (
          <>
            <div className='op-row'>
              <label>Lower Bound</label>
              <input type="number" value={op.params?.lower || 0} onChange={(e)=> onUpdateParam(index,'lower', parseFloat(e.target.value))} />
            </div>
            <div className='op-row'>
              <label>Upper Bound</label>
              <input type="number" value={op.params?.upper || 1} onChange={(e)=> onUpdateParam(index,'upper', parseFloat(e.target.value))} />
            </div>
          </>
        )}

        {op.id === 'count_values' && (
          <div className='op-row'>
            <label>Output Label Name</label>
            <input value={op.params?.label || 'value'} onChange={(e)=> onUpdateParam(index,'label', e.target.value)} placeholder='value' />
          </div>
        )}

        {/* Label filter operations (for Loki) */}
{(op.id === 'label_filter' || op.id === 'label_filter_ip_matches') && (
  <>
    <div className='op-row'>
      <label>Label Name</label>
      <select 
        value={op.params?.label || ''} 
        onChange={(e)=> onUpdateParam(index,'label', e.target.value)}
        className='by-select'
      >
        <option value="">Select label...</option>
        {labelKeys.map(k=> <option key={k} value={k}>{k}</option>)}
      </select>
    </div>
    <div className='op-row'>
      <label>Operator</label>
      <select 
        value={op.params?.op || '='} 
        onChange={(e)=> onUpdateParam(index,'op', e.target.value)}
      >
        <option value="=">=</option>
        <option value="!=">!=</option>
        <option value="=~">=~ (regex)</option>
        <option value="!~">!~ (not regex)</option>
        <option value=">">{'>'}</option>
        <option value="<">{'<'}</option>
        <option value=">=">{'>='}</option>
        <option value="<=">{'<='}</option>
      </select>
    </div>
    <div className='op-row'>
      <label>{op.id === 'label_filter_ip_matches' ? 'IP/CIDR' : 'Value'}</label>
      <input 
        value={op.params?.value || ''} 
        onChange={(e)=> onUpdateParam(index,'value', e.target.value)} 
        placeholder={op.id === 'label_filter_ip_matches' ? '192.168.1.0/24' : 'value'} 
      />
    </div>
  </>
)}

{/* Unwrap operations (for Loki) */}
{(op.id === 'unwrap' || op.id === 'unwrap_duration' || op.id === 'unwrap_bytes') && (
  <div className='op-row'>
    <label>Label to Unwrap</label>
    <select 
      value={op.params?.value || op.params?.label || ''} 
      onChange={(e)=> onUpdateParam(index,'value', e.target.value)}
      className='by-select'
    >
      <option value="">Select label...</option>
      {labelKeys.map(k=> <option key={k} value={k}>{k}</option>)}
    </select>
  </div>
)}

{/* Drop/Keep operations (for Loki) */}
{(op.id === 'drop' || op.id === 'keep') && (
  <div className='op-row'>
    <label>Labels (comma-separated)</label>
    <input 
      value={op.params?.value || ''} 
      onChange={(e)=> onUpdateParam(index,'value', e.target.value)} 
      placeholder='label1, label2, label3' 
    />
  </div>
)}

{/* Label format (for Loki) */}
{op.id === 'label_format' && (
  <div className='op-row'>
    <label>Format Expression</label>
    <input 
      value={op.params?.value || ''} 
      onChange={(e)=> onUpdateParam(index,'value', e.target.value)} 
      placeholder='new_label="{{ .old_label }}"' 
    />
  </div>
)}

{/* Line filter IP matches (for Loki) */}
{op.id === 'line_filter_ip_matches' && (
  <div className='op-row'>
    <label>IP/CIDR Range</label>
    <input 
      value={op.params?.value || ''} 
      onChange={(e)=> onUpdateParam(index,'value', e.target.value)} 
      placeholder='192.168.1.0/24 or 10.0.0.1' 
    />
  </div>
)}
      </div>
    </div>
  );
}
